import groupBy from 'lodash/groupBy';

import { useEffect, useState } from 'react';
import type {
  Course,
  LearnEntityResponseCollection,
  LearnItemRelationResponseCollection,
  LearnPage,
} from '@edwin-group/people-graphql';
import CoursesSkeleton, { HighlightSkeleton } from '../courses/Courses.skeleton';
import AnimatedList, { courseWithLearnProps } from './animatedList';
import { fetchLearn, getPage } from '@/services/learn/learn.service';
import { getCourseCategoriesByLearn } from '@/utils/learn';
import Container from '@/components/Container';
import Navigation, { NavigationSkeleton } from '@/components/ui/navigation';
import { navigationLearn } from '../constants';
import Highlight from '@/components/Highlight';

function CoursesInProgressTab() {
  const [learn, setLearn] = useState<LearnEntityResponseCollection | null>(null);
  const [learnPage, setLearnPage] = useState<LearnPage | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    (async () => {
      setLoading(true);

      const [pageResponse, learnResult] = await Promise.all([getPage(), fetchLearn({ completed: false })]);

      setLearnPage(pageResponse.page);
      setLearn(learnResult as unknown as LearnEntityResponseCollection);
      setLoading(false);
    })();
  }, []);

  const courses =
    learn?.data?.map((l) => ({
      ...l?.attributes?.course?.data,
      learn: l?.attributes,
      learnCount: (l?.attributes?.learn_items as unknown as LearnItemRelationResponseCollection)?.data.length || 0,
    })) || [];

  const coursesData = courses?.map((c) => ({
    ...c,
    category: c?.attributes?.course_category?.data?.attributes?.name,
  }));

  const categories = getCourseCategoriesByLearn(courses as Course[]);
  const courseGroupedByCategory = groupBy(coursesData, 'category') || {};

  if (loading) {
    return (
      <>
        <HighlightSkeleton />
        <NavigationSkeleton />
        <Container className="last-divider-hidden mt-sh-60 min-h-40">
          <CoursesSkeleton />
        </Container>
      </>
    );
  }

  return (
    <>
      {learnPage?.learnHighlight && (learnPage.learnHighlight.title || learnPage.learnHighlight.description) && (
        <Highlight
          title={learnPage.learnHighlight.title ?? ''}
          sectionTitle="Learn"
          description={learnPage.learnHighlight.description ?? ''}
          image={learnPage.learnHighlight.image?.data?.attributes?.url}
          imageAlt={
            learnPage.learnHighlight.image?.data?.attributes?.alternativeText ??
            learnPage.learnHighlight.title ??
            'Learn section'
          }
        />
      )}
      <Navigation title="Learn" items={navigationLearn} />
      <Container className="last-divider-hidden mt-sh-30 min-h-40">
        {!learn?.data?.length ? (
          <div className="col-span-12 mt-8 text-center">
            {learnPage?.emptyInProgressCoursesMessage || `You don't have any courses in progress yet`}
          </div>
        ) : (
          Object.keys(categories)
            .map((category) => ({
              name: category,
              courses:
                (courseGroupedByCategory[category]?.map((course) => ({
                  ...course?.attributes,
                  learn: {
                    completed: course?.learn?.completed,
                    learnCount: course?.learnCount,
                  },
                })) as courseWithLearnProps[]) || [],
              description: categories[category]?.description || '',
            }))
            .map((category) => <AnimatedList category={category} key={category?.name} />)
        )}
      </Container>
    </>
  );
}

export default CoursesInProgressTab;
